import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useConfig } from '../config/ConfigContext';
import { checkCenterLiveStatus } from '../services/liveDetectionService';

const LiveStatusContext = createContext();

export const useLiveStatus = () => {
  const context = useContext(LiveStatusContext);
  if (!context) {
    throw new Error('useLiveStatus must be used within LiveStatusProvider');
  }
  return context;
};

/**
 * LiveStatusProvider — adaptive live stream detection (v2).
 *
 * Architecture (v2 — after live-detection overhaul, June 2026):
 * - Reads is_live_now from Supabase (fast, no quota)
 * - Supabase Realtime subscription pushes UI updates instantly
 * - Background poll (every 10 min) is a defense-in-depth FALLBACK only
 * - Notifications fire SERVER-SIDE via DB trigger on is_live_now flip
 *   (see migration 20260608_live_detection_v2.sql — no client call here)
 * - YouTube API quota usage moved entirely to server-side cron + WebSub
 *
 * The PRIMARY detection path is now WebSub (sub-second push from YouTube)
 * with youtube_live_monitor cron as backup. This context only READS state.
 *
 * Reduced cadence: background poll lengthened from 10→30 min because
 * server-side detection is now reliable.
 */
const LIVE_POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes when live
// Safety net: ignore is_live_now=true if the row hasn't been touched recently.
// The server-side monitor runs every 5 min — if updated_at is >1h old, the
// monitor is probably broken and the value can't be trusted.
const STALE_UPDATED_AT_HOURS = 1;
const BACKGROUND_POLL_INTERVAL = 30 * 60 * 1000; // 30 min — fallback only; WebSub is primary

export const LiveStatusProvider = ({ children }) => {
  const { config } = useConfig();
  const [hasLiveVideos, setHasLiveVideos] = useState(false);
  const [liveCount, setLiveCount] = useState(0);
  const [liveVideoData, setLiveVideoData] = useState(null);
  const [isChecking, setIsChecking] = useState(false);

  // Track which events we've already checked (don't re-check non-streaming events)
  const checkedEvents = useRef(new Set());
  const pollTimer = useRef(null);
  const hasActiveEventRef = useRef(false);
  const checkYouTubeLiveRef = useRef(null);

  // Read is_live_now from Supabase (no quota cost)
  const checkDbStatus = useCallback(async () => {
    if (!config?.center_id) return;

    try {
      // PRIMARY check: is_live_now=true is authoritative — the server-side
      // monitor (pg_cron every 5 min) sets it false when a stream ends.
      // SAFETY net: also require updated_at within the last hour. If the
      // monitor has been broken for >1h, we'd rather show "no live" than
      // risk a stuck red dot. (Old code used published_at which is when
      // YouTube CREATED the video — broken for scheduled streams whose
      // published_at predates the actual live transition by hours/days.)
      const updatedAtThreshold = new Date(Date.now() - STALE_UPDATED_AT_HOURS * 60 * 60 * 1000).toISOString();
      const { count, error } = await supabase
        .from('videos')
        .select('*', { count: 'exact', head: true })
        .eq('center_id', config.center_id)
        .eq('is_live_now', true)
        .eq('privacy_status', 'public')
        .gte('updated_at', updatedAtThreshold);

      if (!error) {
        setLiveCount(count || 0);
        setHasLiveVideos((count || 0) > 0);

        if ('setAppBadge' in navigator && count > 0) {
          try { await navigator.setAppBadge(); } catch (e) {}
        } else if ('clearAppBadge' in navigator && count === 0) {
          try { await navigator.clearAppBadge(); } catch (e) {}
        }
      }
    } catch (error) {
      console.error('[LiveStatus] DB check error:', error);
    }
  }, [config?.center_id]);

  // YouTube live detection (uses RSS + 1 API unit) — FALLBACK PATH
  const checkYouTubeLive = useCallback(async () => {
    if (!config?.center_id || !config?.youtube_channel_url || isChecking) {
      if (!config?.center_id) console.warn('[LiveStatus] Skipping client check: no center_id');
      if (!config?.youtube_channel_url) console.warn('[LiveStatus] Skipping client check: no youtube_channel_url');
      return;
    }

    setIsChecking(true);
    try {
      const result = await checkCenterLiveStatus(config.center_id, config.youtube_channel_url);

      if (result?.isLive) {
        setHasLiveVideos(true);
        setLiveCount(1);
        setLiveVideoData(result);
        // Notification is fired by DB trigger on is_live_now flip — NOT here.
        // The trigger sees the upsert that liveDetectionService just did and
        // calls send-live-notification with atomic dedup via live_notification_sent_at.
        console.log('[LiveStatus] Live detected via client polling; DB trigger will notify');
        return true; // Signal: keep polling
      } else {
        // Re-read DB in case it was updated by the detection
        await checkDbStatus();
        return false; // Signal: stop polling for this event
      }
    } catch (e) {
      console.error('[LiveStatus] YouTube check error:', e);
      return false;
    } finally {
      setIsChecking(false);
    }
  }, [config?.center_id, config?.youtube_channel_url, isChecking, checkDbStatus]);

  // Keep ref in sync for background poll (avoids dependency array churn)
  checkYouTubeLiveRef.current = checkYouTubeLive;

  // Adaptive polling: called when a "Happening Now" event is active
  const onActiveEventChange = useCallback(async (hasActiveEvent, eventId) => {
    hasActiveEventRef.current = hasActiveEvent;

    // Clear existing poll timer
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }

    if (!hasActiveEvent || !config?.youtube_channel_url) return;

    // Skip if we already checked this event and found no live stream
    const eventKey = eventId || 'default';
    if (checkedEvents.current.has(eventKey)) {
      console.log('[LiveStatus] Already checked this event, skipping');
      return;
    }

    // First check
    const isLive = await checkYouTubeLive();
    checkedEvents.current.add(eventKey);

    if (isLive) {
      // Live found → keep polling every 5 minutes to detect when it ends
      pollTimer.current = setInterval(async () => {
        const stillLive = await checkYouTubeLive();
        if (!stillLive && pollTimer.current) {
          clearInterval(pollTimer.current);
          pollTimer.current = null;
        }
      }, LIVE_POLL_INTERVAL);
    }
    // If not live → don't poll. The Supabase real-time subscription is the backup.
  }, [config?.youtube_channel_url, checkYouTubeLive]);

  // Check DB status on mount and periodically (30s, no quota)
  useEffect(() => {
    if (config?.video_options_enabled && config?.center_id) {
      checkDbStatus();
      const interval = setInterval(checkDbStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [config, checkDbStatus]);

  // Supabase real-time subscription
  useEffect(() => {
    if (!config?.center_id) return;

    const subscription = supabase
      .channel('live-status')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'videos',
          filter: `center_id=eq.${config.center_id}`
        },
        () => { checkDbStatus(); }
      )
      .subscribe();

    return () => { subscription.unsubscribe(); };
  }, [config?.center_id, checkDbStatus]);

  // Cleanup poll timer
  useEffect(() => {
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, []);

  // Background periodic live check — catches spontaneous streams without calendar events.
  // Complements event-driven detection: when no "Happening Now" event exists,
  // this ensures live streams are still detected within ~10 minutes.
  // Quota cost: ~1 API unit per 10 minutes = ~6/hour while app is open.
  useEffect(() => {
    if (!config?.video_options_enabled || !config?.youtube_channel_url || !config?.center_id) return;

    const backgroundCheck = () => {
      // Skip when event-driven polling is already active (avoids duplicate API calls)
      if (!hasActiveEventRef.current && checkYouTubeLiveRef.current) {
        checkYouTubeLiveRef.current();
      }
    };

    // Initial check 30s after mount
    const initialTimer = setTimeout(backgroundCheck, 30000);
    // Then every 10 minutes
    const bgInterval = setInterval(backgroundCheck, BACKGROUND_POLL_INTERVAL);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(bgInterval);
    };
  }, [config?.video_options_enabled, config?.youtube_channel_url, config?.center_id]);

  const value = {
    hasLiveVideos,
    liveCount,
    liveVideoData,
    checkLiveStatus: checkDbStatus,
    onActiveEventChange, // Called by RichHomePage when event status changes
  };

  return (
    <LiveStatusContext.Provider value={value}>
      {children}
    </LiveStatusContext.Provider>
  );
};
